import { useQuery } from '@tanstack/react-query';
import { Users, BookOpen, GraduationCap, TrendingUp } from 'lucide-react';
import { api } from '@/lib/api';
import { Link } from 'react-router-dom';

export function AdminDashboard() {
  const { data: analyticsData } = useQuery({
    queryKey: ['dashboard-analytics'],
    queryFn: () => api.getDashboardAnalytics(),
  });

  const stats = (analyticsData as any)?.overallStats;
  const recentEnrollments = ((analyticsData as any)?.recentEnrollments || []) as any[];

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
        <p className="text-gray-600 mt-1">Overview of your LMS platform</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <div className="text-2xl font-bold">{stats?.total_learners || 0}</div>
              <div className="text-gray-600 text-sm">Total Learners</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <div className="text-2xl font-bold">{stats?.published_courses || 0}</div>
              <div className="text-gray-600 text-sm">Published Courses</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <GraduationCap className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <div className="text-2xl font-bold">{stats?.total_enrollments || 0}</div>
              <div className="text-gray-600 text-sm">Total Enrollments</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <div className="text-2xl font-bold">{stats?.total_completions || 0}</div>
              <div className="text-gray-600 text-sm">Completions</div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4">Recent Enrollments</h2>
        {recentEnrollments.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No recent enrollments</p>
        ) : (
          <div className="space-y-4">
            {recentEnrollments.map((enrollment: any, index: number) => (
              <div key={index} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-medium">
                    {enrollment.user_name?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <div>
                    <div className="font-medium">{enrollment.user_name}</div>
                    <div className="text-sm text-gray-600">
                      enrolled in <span className="font-medium">{enrollment.course_title}</span>
                    </div>
                  </div>
                </div>
                <div className="text-sm text-gray-500">
                  {new Date(enrollment.enrolled_at * 1000).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Link
          to="/admin/users"
          className="bg-white rounded-lg border-2 border-gray-200 p-6 hover:border-red-300 hover:shadow-md transition-all"
        >
          <Users className="w-8 h-8 text-red-600 mb-3" />
          <h3 className="font-semibold text-lg mb-2">Manage Users</h3>
          <p className="text-gray-600 text-sm">View and manage user accounts, roles, and permissions</p>
        </Link>

        <Link
          to="/admin/courses"
          className="bg-white rounded-lg border-2 border-gray-200 p-6 hover:border-red-300 hover:shadow-md transition-all"
        >
          <BookOpen className="w-8 h-8 text-red-600 mb-3" />
          <h3 className="font-semibold text-lg mb-2">Manage Courses</h3>
          <p className="text-gray-600 text-sm">Oversee all courses and enrollments</p>
        </Link>

        <Link
          to="/author"
          className="bg-white rounded-lg border-2 border-gray-200 p-6 hover:border-red-300 hover:shadow-md transition-all"
        >
          <GraduationCap className="w-8 h-8 text-red-600 mb-3" />
          <h3 className="font-semibold text-lg mb-2">Create Content</h3>
          <p className="text-gray-600 text-sm">Go to author portal to create new courses</p>
        </Link>
      </div>
    </div>
  );
}
